import React, { useState, useEffect } from "react";
import AgentListView, { Agent } from "./AgentListView";
import AgentMonitoringView from "./AgentMonitoringView";
import { TeamsProvider, useTeamsContext, type AgentDetails, getAgentDetailKey } from "./hooks/useTeamsContext";
import { fetchAgents, mapApiAgentToAgent } from "./services/api";
import { environment } from "../environments/environment";
import "../styles.scss";

// Simple error boundary so Teams mobile (webview) shows runtime errors
// instead of a blank white screen.
class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log for debugging in browser devtools / Teams webview
    // eslint-disable-next-line no-console
    console.error("Agent homepage crashed:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
          <h2 style={{ marginBottom: 12 }}>Something went wrong in Agent Monitoring.</h2>
          <p style={{ marginBottom: 8 }}>Please share this message with the Loops dev team:</p>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#fff1f0",
              border: "1px solid #ffa39e",
              borderRadius: 4,
              padding: 12,
              fontSize: 12,
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {this.state.error?.message}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppInner: React.FC = () => {
  const { context: teamsContext, initialized, loading, error, cubeJsToken, authToken, appId, agentDetails, agentDetailsList } =
    useTeamsContext();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [selectedAgentDetailKey, setSelectedAgentDetailKey] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState<boolean>(true);

  const debugPanel =
    environment.isLocal && showDebug ? (
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          maxHeight: "35vh",
          overflow: "auto",
          background: "#1e1e1e",
          color: "#d4d4d4",
          fontFamily: "monospace",
          fontSize: 11,
          padding: 8,
          zIndex: 99999,
          borderTop: "2px solid #ff0",
        }}
      >
        <button
          type="button"
          style={{
            position: "absolute",
            top: 4,
            right: 8,
            border: "1px solid #555",
            background: "#2d2d2d",
            color: "#fff",
            padding: "2px 6px",
            fontSize: 10,
            cursor: "pointer",
          }}
          onClick={() => setShowDebug(false)}
        >
          hide
        </button>
        <div>
          <div>
            <strong>1. teamsContext:</strong>{" "}
            {teamsContext == null ? "null" : JSON.stringify(teamsContext)}
          </div>
          <div>
            <strong>2. authToken:</strong>{" "}
            {authToken == null ? "null" : authToken ? "set" : "falsy"} |{" "}
            <strong>cubeJsToken:</strong>{" "}
            {cubeJsToken == null ? "null" : cubeJsToken ? "set" : "falsy"}
          </div>
          <div>
            <strong>3. agents.length:</strong> {agents.length}
          </div>
          <div>
            <strong>4. agentDetails:</strong>{" "}
            {agentDetails == null ? "null" : JSON.stringify(agentDetails)}
          </div>
          <div>
            <strong>5. agentDetailsList:</strong>{" "}
            {agentDetailsList.length === 0 ? "[]" : JSON.stringify(agentDetailsList)}
          </div>
        </div>
      </div>
    ) : null;

  const resolvedAppId = appId ?? (environment as { appId?: string }).appId ?? "2";
  const resolvedAuthToken =
    authToken ??
    (environment.isLocal ? (environment as { authToken?: string }).authToken : undefined);
  const teamId = (teamsContext as { team?: { groupId?: string } } | null)?.team?.groupId;

  useEffect(() => {
    const token = resolvedAuthToken;
    if (!token) {
      setAgentsLoading(false);
      setAgentsError("Authenticating....");
      return;
    }
    let cancelled = false;
    setAgentsLoading(true);
    setAgentsError(null);
    fetchAgents(resolvedAppId, token, teamId, environment.envName)
      .then((list) => {
        if (cancelled) return;
        const mapped = list.map(mapApiAgentToAgent);
        setAgents([...mapped]);
      })
      .catch((err) => {
        if (!cancelled) {
          setAgentsError(err instanceof Error ? err.message : "Failed to load agents");
        }
      })
      .finally(() => {
        if (!cancelled) setAgentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedAuthToken, resolvedAppId, teamId]);

  const applyAgentDetailToSelectedAgent = (detail: AgentDetails, agentList: Agent[]) => {
    const agent = agentList.find((a) => a.agentUUID === detail.agentUUID);
    if (agent) {
      setSelectedAgent({ ...agent, agentUUID: detail.agentUserId });
    } else {
      setSelectedAgent({
        agentUUID: detail.agentUserId,
        name: detail.agentName,
        iconUrl: "1.svg",
      } as Agent);
    }
    setSelectedAgentDetailKey(getAgentDetailKey(detail));
  };

  // Update selectedAgent when agentDetails loads (e.g. from registerTeamsChannel)
  useEffect(() => {
    if (agents?.length > 0) {
      if (agentDetails?.agentUserId) {
        const activeDetail =
          agentDetailsList.find((detail) => getAgentDetailKey(detail) === selectedAgentDetailKey) ??
          agentDetails;
        applyAgentDetailToSelectedAgent(activeDetail, agents);
      } else if (environment.isLocal && environment.selectedAgent) {
        const fromList = agents.find((a) => a.id === environment.selectedAgent!.id);
        setSelectedAgent(fromList ?? mapApiAgentToAgent(environment.selectedAgent));
      } else {
        setSelectedAgent(agents[0]);
      }
    } else if (agentDetails?.agentUserId && !agents?.length) {
      applyAgentDetailToSelectedAgent(agentDetails, agents);
    }
  }, [agentDetails?.agentUserId, agents, agentDetailsList]);

  const handleAgentDetailSelect = (detailKey: string) => {
    const detail = agentDetailsList.find((item) => getAgentDetailKey(item) === detailKey);
    if (!detail) return;
    applyAgentDetailToSelectedAgent(detail, agents);
  };


  // Agents loading or error
  if (agentsLoading) {
    return (
      <>
        <div style={{ padding: 24, textAlign: "center" }}>Loading agents…</div>
        {debugPanel}
      </>
    );
  }
  if (resolvedAuthToken && agentsError && !loading && !agentsLoading) {
    return (
      <>
        <div style={{ padding: 24, textAlign: "center", color: "red" }}>
          Failed to load agents: {agentsError}
        </div>
        {debugPanel}
      </>
    );
  }

  // Outside Teams context: behave as before, ignore cubeJsToken state
  if (!initialized) {
    if (!selectedAgent) {
      return (
        <>
          <AgentListView agents={agents} onSelectAgent={(agent) => setSelectedAgent(agent)} />
          {debugPanel}
        </>
      );
    }
    return (
      <>
        <AgentMonitoringView
          selectedAgent={selectedAgent}
          appId={resolvedAppId}
          agentDetailsList={agentDetailsList}
          selectedAgentDetailKey={selectedAgentDetailKey}
          onAgentDetailSelect={handleAgentDetailSelect}
        />
        {debugPanel}
      </>
    );
  }

  // In Teams context: wait for auth token (REST API) / show errors
  if (loading || (!authToken && !error)) {
    return (
      <>
        <div style={{ padding: 24, textAlign: "center" }}>Connecting to Microsoft Teams…</div>
        {debugPanel}
      </>
    );
  }

  if (error) {
    return (
      <>
        <div
          style={{
            padding: 24,
            fontFamily:
              "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          <h2 style={{ marginBottom: 12 }}>Unable to load Agent Homepage</h2>
          <p style={{ marginBottom: 8 }}>
            Please share the details below with your support team.
          </p>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#fff1f0",
              border: "1px solid #ffa39e",
              borderRadius: 4,
              padding: 12,
              fontSize: 12,
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {error.message}
          </pre>
        </div>
        {debugPanel}
      </>
    );
  }

  if (!selectedAgent) {
    return (
      <>
        <AgentListView agents={agents} onSelectAgent={(agent) => setSelectedAgent(agent)} />
        {debugPanel}
      </>
    );
  }

  return (
    <>
      <AgentMonitoringView
        selectedAgent={selectedAgent}
        appId={resolvedAppId}
        agentDetailsList={agentDetailsList}
        selectedAgentDetailKey={selectedAgentDetailKey}
        onAgentDetailSelect={handleAgentDetailSelect}
      />
      {debugPanel}
    </>
  );
};

export function App() {
  return (
    <TeamsProvider>
      <AppErrorBoundary>
        <AppInner />
      </AppErrorBoundary>
    </TeamsProvider>
  );
}

export default App;
